import React from 'react';

import {InlineEditableTable} from '../../../shared/components';
import type {IFinancialHistoryTableProps} from '../financials.types';

export const FinancialHistoryTable: React.FC<IFinancialHistoryTableProps> = ({
  financialSummaries,
  isSavingFinancialHistory,
  yearOptions,
  monthOptions,
  onSummaryFieldChange,
}) => (
  <InlineEditableTable className="financials-summary__table-wrap">
    <table className="financials-summary-table">
      <thead>
        <tr>
          <th scope="col">Metric</th>
          {financialSummaries.map((record) => (
            <th key={`summary-year-${record.year}`} scope="col">
              <div className="financials-summary-table__year-head">
                <span>{record.year}</span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">Revenue</th>
          {financialSummaries.map((record) => (
            <td key={`summary-revenue-${record.year}`}>
              <input
                type="text"
                inputMode="decimal"
                className="financials-summary-table__cell-input"
                value={record.revenue}
                onChange={(event) =>
                  onSummaryFieldChange(
                    record.year,
                    'revenue',
                    event.target.value,
                  )
                }
                disabled={isSavingFinancialHistory}
                placeholder="0.00"
                aria-label={`Revenue for ${record.year}`}
              />
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Costs</th>
          {financialSummaries.map((record) => (
            <td key={`summary-costs-${record.year}`}>
              <input
                type="text"
                inputMode="decimal"
                className="financials-summary-table__cell-input"
                value={record.expenses}
                onChange={(event) =>
                  onSummaryFieldChange(
                    record.year,
                    'expenses',
                    event.target.value,
                  )
                }
                disabled={isSavingFinancialHistory}
                placeholder="0.00"
                aria-label={`Costs for ${record.year}`}
              />
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Operating costs</th>
          {financialSummaries.map((record) => (
            <td key={`summary-opex-${record.year}`}>
              <input
                type="text"
                inputMode="decimal"
                className="financials-summary-table__cell-input"
                value={record.operatingCosts}
                onChange={(event) =>
                  onSummaryFieldChange(
                    record.year,
                    'operatingCosts',
                    event.target.value,
                  )
                }
                disabled={isSavingFinancialHistory}
                placeholder="0.00"
                aria-label={`Operating costs for ${record.year}`}
              />
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Profit</th>
          {financialSummaries.map((record) => (
            <td key={`summary-profit-${record.year}`}>
              <input
                type="text"
                className="financials-summary-table__cell-input is-readonly"
                value={record.profitLoss}
                readOnly
                tabIndex={-1}
                aria-label={`Profit for ${record.year}`}
              />
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Financial year end (year)</th>
          {financialSummaries.map((record) => (
            <td key={`summary-year-end-${record.year}`}>
              <select
                className="financials-summary-table__cell-select"
                value={record.financialYearEnd ?? ''}
                onChange={(event) =>
                  onSummaryFieldChange(
                    record.year,
                    'financialYearEnd',
                    event.target.value,
                  )
                }
                disabled={isSavingFinancialHistory}
                aria-label={`Financial year end year for ${record.year}`}
              >
                <option value="">Select year</option>
                {yearOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Financial year end month</th>
          {financialSummaries.map((record) => (
            <td key={`summary-year-end-month-${record.year}`}>
              <select
                className="financials-summary-table__cell-select"
                value={record.financialYearEndMonth ?? ''}
                onChange={(event) =>
                  onSummaryFieldChange(
                    record.year,
                    'financialYearEndMonth',
                    event.target.value,
                  )
                }
                disabled={isSavingFinancialHistory}
                aria-label={`Financial year end month for ${record.year}`}
              >
                <option value="">Select month</option>
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Notes</th>
          {financialSummaries.map((record) => (
            <td key={`summary-notes-${record.year}`}>
              <textarea
                className="financials-summary-table__cell-textarea"
                rows={3}
                value={record.notes}
                onChange={(event) =>
                  onSummaryFieldChange(record.year, 'notes', event.target.value)
                }
                disabled={isSavingFinancialHistory}
                placeholder="Add notes"
                aria-label={`Notes for ${record.year}`}
              />
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  </InlineEditableTable>
);
